import { entityReferenceStore } from 'store/EntityReferenceStore';
import { PagedCollection }      from 'modelx-jts-common';
import EntityStatusHistoryModel from 'models/fondationsModels/EntityStatusHistoryModel';
import { ApiCollection }        from 'modelx-jts-common';
import UserModel                from 'models/fondationsModels/UserModel';
import { action }               from 'mobx';
import { observable }           from 'mobx';
import EntityModel              from 'models/fondationsModels/EntityModel';
import { getIdFromUrn }         from 'tools/lsdTools';
import AccountLsdModel          from 'models/AccountLsdModel';
import BankDetailModel          from 'models/BankDetailModel';
import ContactModel             from 'models/networkModels/ContactModel';
import EntityAddressModel       from 'models/fondationsModels/EntityAddressModel';
import ExpenseBudgetModel       from 'models/ExpenseBudgetModel';
import ServiceTagModel          from 'models/fondationsModels/ServiceTagModel';
import SupplierModel            from 'models/SupplierModel';
import { getUniqueDefinedValues } from 'tools/jsTools';
import { computed } from 'mobx';

class EntityDashboardStore {
	@observable public model;
	@observable public accountCollection = new ApiCollection(AccountLsdModel);
	@observable public bankDetailCollection = new ApiCollection(BankDetailModel);
	@observable public contactCollection = new ApiCollection(ContactModel);
	@observable public entityAddressCollection = new ApiCollection(EntityAddressModel);
	@observable public entityStatusHistoryPagedCollection = new PagedCollection(EntityStatusHistoryModel);
	@observable public expenseCollection = new ApiCollection(ExpenseBudgetModel);
	@observable public serviceTagsCollection = new ApiCollection(ServiceTagModel);
	@observable public supplierCollection = new ApiCollection(SupplierModel);
	@observable public userCollection = new ApiCollection(UserModel);

	@action
	public async loadForEntity(model: EntityModel) {
		this.setModel(model);

		this.expenseCollection.setFilter('expenseSupplierContacts.entityUrn', model.urn);

		await Promise.all([
			this.contactCollection
				.setFilter('ownerUser.userUrn', model.urn)
				.list(),

			this.entityAddressCollection
				.setFilters({
					entity: model.id,
				})
				.list(),

			// Source pour usersIdsFromHistory
			this.entityStatusHistoryPagedCollection
				.setFilters({
					entity: model.id,
				})
				.addSorts({ createdAt: false, id: false })
				.setLimit(5)
				.list(),

			this.serviceTagsCollection
				.setFilter('entityServiceTags.entity', model.id)
				.list(),

			// Source pour usersIdsFromAccounts (via bankDetails)
			this.supplierCollection
				.setFilter('entityUrn', model.urn)
				.list()
				.then(async () => {
					if (!this.supplier.id) {
						return;
					}

					await this.bankDetailCollection
						.setFilter('supplier', this.supplier.id)
						.list();

					const accountsIds = getUniqueDefinedValues(
						this.bankDetailCollection.map((bd: BankDetailModel) => bd.createdById),
						this.bankDetailCollection.map((bd: BankDetailModel) => bd.updatedById),
					);

					if (accountsIds.length) {
						await this.accountCollection
							.setFilter('id', accountsIds)
							.list();
					}
				})
		]);

		const usersIdsFromAccounts = this.accountCollection.map((a: AccountLsdModel) => a.userId);

		const usersIdsFromHistory = this.entityStatusHistoryPagedCollection.map(
			(h: EntityStatusHistoryModel) => getIdFromUrn(h.get('createdBy'))
		);

		const usersIds = getUniqueDefinedValues(
			usersIdsFromAccounts,
			usersIdsFromHistory,
		);

		if (usersIds.length) {
			await this.userCollection
				.setFilter('id', usersIds)
				.list();
		}
	}

	@computed
	public get supplier(): SupplierModel {
		let supplier = this.supplierCollection.find(
			(s: SupplierModel) => s.entityUrn === this.model?.urn
		) as SupplierModel;

		if (!supplier) {
			supplier = new SupplierModel();
		}

		return supplier;
	}

	@action
	public setModel = (model: EntityModel) => {
		this.model = model;
	}
}

export const entityDashboardStore = new EntityDashboardStore();